import tokens from '@kluein/design-tokens';
import { ActionIcon, Box, Modal, Tooltip } from '@mantine/core';
import { MagnifyingGlassPlus } from '@phosphor-icons/react';
import { type IframeHTMLAttributes, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IFRAME_SANDBOX } from 'klue-html/iframe';
import TEST_IDS from 'test-ids';

export type EmbedContentProps = IframeHTMLAttributes<HTMLIFrameElement>;

export default function EmbedContent({
  src,
  title,
  // Dropped from the spread so an embed's own sandbox can't override ours.
  sandbox: _ignoredSandbox,
  ...iframeProps
}: EmbedContentProps): ReactNode {
  const { t } = useTranslation(['Card']);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);

  const isButtonVisible = (isHovered || isFocused) && !isZoomModalOpen;

  const iframeTitle = title || t('Card:embedTitle');
  const tooltipLabel = t('Card:zoomEmbed');

  const handleOpenZoom = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!src) return;
    setIsZoomModalOpen(true);
  };

  const handleCloseZoom = () => {
    setIsZoomModalOpen(false);
  };

  return (
    <>
      <Box
        pos="relative"
        display="inline-block"
        w="100%"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-test-id={TEST_IDS.klueCard.transform.embed.container}
      >
        <iframe
          {...iframeProps}
          src={src}
          title={iframeTitle}
          loading="lazy"
          sandbox={IFRAME_SANDBOX}
        />
        {src && (
          <Tooltip label={tooltipLabel} position="left" withArrow>
            <ActionIcon
              variant="filled"
              color="dark"
              size="md"
              radius="sm"
              onClick={handleOpenZoom}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              aria-label={tooltipLabel}
              data-test-id={TEST_IDS.klueCard.transform.embed.zoomButton}
              style={{
                position: 'absolute',
                top: tokens.spacing.xsmall,
                right: tokens.spacing.xsmall,
                opacity: isButtonVisible ? 1 : 0,
                pointerEvents: isButtonVisible ? 'auto' : 'none',
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              <MagnifyingGlassPlus size={20} weight="regular" />
            </ActionIcon>
          </Tooltip>
        )}
      </Box>

      {src && (
        <Modal
          opened={isZoomModalOpen}
          onClose={handleCloseZoom}
          size="90%"
          centered
          padding={0}
          withCloseButton={false}
          data-test-id={TEST_IDS.klueCard.transform.embed.zoomModal}
        >
          <Box style={{ width: '100%', height: '90vh' }}>
            <iframe
              {...iframeProps}
              src={src}
              title={iframeTitle}
              loading="lazy"
              sandbox={IFRAME_SANDBOX}
              style={{
                width: '100%',
                height: '100%',
                border: 0,
                display: 'block',
              }}
            />
          </Box>
        </Modal>
      )}
    </>
  );
}
